import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useParams, Link } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import rehypeRaw from 'rehype-raw';
import { Loader2, ArrowLeft, Calendar, Tag, User, Clock, Share2, Check, Sparkles } from 'lucide-react';
import { toast } from '../hooks/use-toast';
import { LOGO_URL } from '../mock/mock';

import { API } from '../config';
import { processMarkdownContent } from '../utils/markdownHelper';

const StoryDetail = () => {
  const { slug } = useParams();
  const [story, setStory] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    setLoading(true);
    axios.get(`${API}/stories/${slug}`)
      .then(r => setStory(r.data))
      .catch(e => setErr(e.response?.data?.detail || 'Story not found'))
      .finally(() => setLoading(false));
  }, [slug]);

  // Push SEO and OpenGraph meta tags client-side for WhatsApp / Social URL sharing
  useEffect(() => {
    if (!story) return;
    const pageTitle = `${story.meta_title || story.title} – TrainPanda`;
    document.title = pageTitle;

    const setMeta = (name, content, attr = 'name') => {
      let el = document.querySelector(`meta[${attr}="${name}"]`);
      if (!el) {
        el = document.createElement('meta');
        el.setAttribute(attr, name);
        document.head.appendChild(el);
      }
      el.setAttribute('content', content || '');
    };

    const shareImage = story.hero_image || LOGO_URL;
    const shareDesc = story.meta_description || story.excerpt || `Read complete train journey guide for ${story.title} on TrainPanda.`;

    setMeta('description', shareDesc);
    setMeta('og:title', story.meta_title || story.title, 'property');
    setMeta('og:description', shareDesc, 'property');
    setMeta('og:image', shareImage, 'property');
    setMeta('og:url', window.location.href, 'property');
    setMeta('og:type', 'article', 'property');
    setMeta('og:site_name', 'TrainPanda', 'property');
    setMeta('twitter:card', 'summary_large_image');
    setMeta('twitter:title', story.meta_title || story.title);
    setMeta('twitter:description', shareDesc);
    setMeta('twitter:image', shareImage);
  }, [story]);

  const handleShare = () => {
    const url = window.location.href;
    const shareData = {
      title: story.title,
      text: story.excerpt || story.title,
      url: url,
    };

    if (navigator.share && navigator.canShare && navigator.canShare(shareData)) {
      navigator.share(shareData).catch(() => {});
    } else {
      navigator.clipboard.writeText(url);
      setCopied(true);
      toast({ title: 'Link copied to clipboard!' });
      setTimeout(() => setCopied(false), 2000);
    }
  };

  if (loading) {
    return (
      <div className="max-w-4xl mx-auto px-4 py-8 md:py-12 space-y-6">
        {/* Navigation & Share placeholder */}
        <div className="flex items-center justify-between">
          <div className="h-5 w-36 rounded-lg shimmer-wave" />
          <div className="h-8 w-28 rounded-full shimmer-wave" />
        </div>

        {/* White Card Container Skeleton */}
        <div className="bg-white rounded-3xl p-6 md:p-10 border border-gray-100 card-shadow space-y-6">
          <div className="h-6 w-32 rounded-full shimmer-wave-purple" />
          <div className="space-y-3">
            <div className="h-10 rounded-2xl w-5/6 shimmer-wave" />
            <div className="h-10 rounded-2xl w-3/4 shimmer-wave" />
          </div>
          <div className="h-5 rounded-xl w-2/3 shimmer-wave" />

          <div className="flex items-center gap-4 py-3 border-y border-gray-100">
            <div className="h-4 w-36 rounded-md shimmer-wave" />
            <div className="h-4 w-28 rounded-md shimmer-wave" />
            <div className="h-4 w-20 rounded-md shimmer-wave" />
          </div>

          <div className="aspect-[16/9] w-full rounded-2xl shimmer-wave" />

          <div className="space-y-4 pt-4">
            <div className="h-4 rounded-md w-full shimmer-wave" />
            <div className="h-4 rounded-md w-full shimmer-wave" />
            <div className="h-4 rounded-md w-4/5 shimmer-wave" />
            <div className="h-8 rounded-xl w-1/2 mt-8 shimmer-wave" />
            <div className="h-4 rounded-md w-full shimmer-wave" />
            <div className="h-4 rounded-md w-5/6 shimmer-wave" />
          </div>
        </div>
      </div>
    );
  }
  if (err) return (
    <div className="py-20 text-center max-w-md mx-auto px-4">
      <div className="text-gray-600 font-bold text-lg mb-2">{err}</div>
      <p className="text-sm text-gray-400 mb-6">The story you are looking for might have been moved or unpublished.</p>
      <Link to="/stories" className="bg-gradient-to-r from-tp-violet to-tp-violet-deep text-white font-bold px-5 py-2.5 rounded-xl inline-flex items-center gap-1.5 shadow-md">
        <ArrowLeft size={16} /> Back to all stories
      </Link>
    </div>
  );
  if (!story) return null;

  const isHtml = story.content_type === 'html' || (story.content_html && !story.content_markdown);

  return (
    <article className="max-w-4xl mx-auto px-4 py-6 md:py-10">
      {/* Breadcrumbs & Share */}
      <div className="flex items-center justify-between gap-4 mb-6">
        <Link to="/stories" className="inline-flex items-center gap-1.5 text-xs font-extrabold text-gray-500 hover:text-tp-violet transition-colors">
          <ArrowLeft size={14} /> Back to All Stories
        </Link>

        <button
          onClick={handleShare}
          className="inline-flex items-center gap-1.5 text-xs font-extrabold bg-tp-violet-soft text-tp-violet-deep hover:bg-tp-violet hover:text-white px-3 py-1.5 rounded-full transition-all"
        >
          {copied ? <Check size={13} /> : <Share2 size={13} />}
          {copied ? 'Link Copied' : 'Share Story'}
        </button>
      </div>

      {/* Category Pill */}
      <div className="inline-flex items-center gap-1.5 bg-tp-coral-soft text-tp-coral-deep px-3 py-1 rounded-full text-xs font-extrabold mb-3">
        <Sparkles size={12} /> {story.category || 'Travel Story'}
      </div>

      {/* Title */}
      <h1 data-testid="story-title" className="text-3xl md:text-4xl lg:text-5xl font-black text-[#0B2E5C] leading-tight tracking-tight">
        {story.title}
      </h1>

      {/* Excerpt if present */}
      {story.excerpt && (
        <p className="text-base md:text-lg text-gray-600 mt-3 font-medium leading-relaxed">
          {story.excerpt}
        </p>
      )}

      {/* Meta details bar */}
      <div className="flex flex-wrap items-center gap-4 mt-5 pb-5 border-b border-gray-100 text-xs text-gray-500 font-semibold">
        <span className="inline-flex items-center gap-1 text-gray-700">
          <User size={13} className="text-tp-violet" /> {story.author || 'TrainPanda Editorial'}
        </span>
        <span>•</span>
        <span className="inline-flex items-center gap-1">
          <Calendar size={13} className="text-tp-violet" />
          {new Date(story.created_at).toLocaleDateString(undefined, { month: 'long', day: 'numeric', year: 'numeric' })}
        </span>
        {story.read_time && (
          <>
            <span>•</span>
            <span className="inline-flex items-center gap-1">
              <Clock size={13} className="text-tp-violet" /> {story.read_time}
            </span>
          </>
        )}
      </div>

      {/* Hero Image */}
      {story.hero_image && (
        <div className="mt-6 rounded-2xl overflow-hidden border border-gray-100 card-shadow bg-gray-100">
          <img
            src={story.hero_image}
            alt={story.title}
            className="w-full max-h-[480px] object-cover"
            onError={(e) => { e.currentTarget.style.display = 'none'; }}
          />
        </div>
      )}

      {/* Content Rendering: Markdown or HTML */}
      <div className="mt-8 story-body">
        {isHtml ? (
          <div
            className="prose prose-slate lg:prose-lg max-w-none prose-headings:text-[#0B2E5C] prose-headings:font-black prose-p:text-gray-700 prose-p:leading-relaxed prose-a:text-tp-coral prose-img:rounded-2xl prose-img:shadow-md"
            dangerouslySetInnerHTML={{ __html: story.content_html || '' }}
          />
        ) : (
          <div className="prose prose-slate lg:prose-lg max-w-none">
            <ReactMarkdown
              remarkPlugins={[remarkGfm]}
              rehypePlugins={[rehypeRaw]}
              components={{
                img: (props) => (
                  <img
                    {...props}
                    className="rounded-2xl my-6 w-full shadow-md object-cover border border-gray-100"
                    loading="lazy"
                    alt={props.alt || ''}
                  />
                ),
                h1: (props) => <h1 className="text-3xl md:text-4xl font-black text-[#0B2E5C] mt-10 mb-5 pb-2 border-b border-gray-100" {...props} />,
                h2: (props) => <h2 className="text-2xl md:text-3xl font-black text-[#0B2E5C] mt-10 mb-4 pb-1 border-b border-gray-100" {...props} />,
                h3: (props) => <h3 className="text-xl md:text-2xl font-extrabold text-[#0B2E5C] mt-8 mb-3" {...props} />,
                h4: (props) => <h4 className="text-lg md:text-xl font-bold text-[#0B2E5C] mt-6 mb-2" {...props} />,
                p: (props) => <p className="text-gray-700 leading-relaxed text-base md:text-lg mb-5 font-normal" {...props} />,
                ul: (props) => <ul className="list-disc pl-6 space-y-2 mb-5 text-gray-700 text-base" {...props} />,
                ol: (props) => <ol className="list-decimal pl-6 space-y-2 mb-5 text-gray-700 text-base" {...props} />,
                a: (props) => <a className="text-tp-coral font-bold underline hover:no-underline" target="_blank" rel="noreferrer" {...props} />,
                blockquote: (props) => (
                  <blockquote className="border-l-4 border-tp-violet bg-tp-violet-soft/40 rounded-r-xl pl-4 py-3 my-6 italic text-gray-800 font-medium" {...props} />
                ),
                strong: (props) => <strong className="text-[#0B2E5C] font-extrabold" {...props} />,
              }}
            >
              {processMarkdownContent(story.content_markdown || '')}
            </ReactMarkdown>
          </div>
        )}
      </div>

      {/* Tags */}
      {story.tags && story.tags.length > 0 && (
        <div className="mt-10 pt-6 border-t border-gray-100 flex items-center gap-2 flex-wrap">
          <span className="text-xs font-extrabold text-gray-400 uppercase tracking-wider">TAGS:</span>
          {story.tags.map((t) => (
            <span key={t} className="inline-flex items-center gap-1 bg-gray-100 hover:bg-gray-200 text-gray-700 px-3 py-1 rounded-full text-xs font-bold transition-colors">
              <Tag size={11} /> {t}
            </span>
          ))}
        </div>
      )}

      {/* Footer / Read More */}
      <div className="mt-12 bg-gradient-to-br from-tp-violet-soft/60 to-white border border-tp-violet/20 rounded-2xl p-6 text-center">
        <h3 className="text-lg font-black text-[#0B2E5C]">Enjoyed this railway travel guide?</h3>
        <p className="text-sm text-gray-600 mt-1 mb-4">Discover more monsoon routes, live train tips &amp; food guides.</p>
        <Link
          to="/stories"
          className="bg-gradient-to-r from-tp-coral to-[#E04A2B] text-white font-extrabold px-6 py-2.5 rounded-xl inline-flex items-center gap-1.5 shadow-md hover:opacity-95 transition-all"
        >
          Explore More Stories →
        </Link>
      </div>
    </article>
  );
};

export default StoryDetail;
